import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import type {Nullish} from 'shared/web';

import {getFinancingEndingOperationItemResultLabel} from './getFinancingEndingOperationItemResultLabel';
import type {FinancingEndingOperationItemRow} from './getFinancingEndingOperationItemRows';

// The byte-order mark lets Excel read the file as UTF-8.
const BOM = '﻿';
// Excel's default list separator for the CZ and SK locales.
const DELIMITER = ';';
const LINE_ENDING = '\r\n';
// RFC 4180 quoting. The comma is quoted too, so a spreadsheet with a comma list separator keeps a
// first-column cell whole.
const NEEDS_QUOTING = /[;,"\r\n]/;
// A spreadsheet runs a cell that starts with one of these as a formula (OWASP CSV injection). With a
// comma list separator, the quote that opens a later cell is a plain character, so the cell splits
// at a comma, a tab or a line break inside it. The guard covers the start of each part after any
// separator, also after spaces or quotes.
const FORMULA_START = /(^|[;,\t\r\n])(?=[ "]*[=+\-@\t\r])/g;
const TEXT_PREFIX = "'";

const neutraliseFormula = (value: string) => value.replaceAll(FORMULA_START, `$1${TEXT_PREFIX}`);

const quote = (value: string) =>
  NEEDS_QUOTING.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

const escapeCell = (value: string) => quote(neutraliseFormula(value));

const toCsvLine = (cells: string[]) => cells.map(escapeCell).join(DELIMITER);

/**
 * The rows in the order of the detail: business case, customer, e-mail, result, reason and processing
 * time. A cell without a value is empty.
 */
export const buildFinancingEndingOperationCsv = (
  rows: FinancingEndingOperationItemRow[],
  formatProcessedAt: (processedAt: string) => string
) => {
  const formatOptional = (value: string | Nullish) =>
    isNil(value) ? '' : formatProcessedAt(value);
  const lines = [
    [
      i18n.t('page.businessCase.labels.businessCase'),
      i18n.t('general.labels.customer'),
      i18n.t('general.labels.email'),
      i18n.t('entity.businessCase.financingEnding.operation.column.result'),
      i18n.t('general.labels.reason'),
      i18n.t('entity.businessCase.financingEnding.operation.column.processedAt'),
    ],
    ...rows.map(({item, result, reason}) => [
      item.recordLabel ?? item.recordId,
      item.recipientName ?? '',
      item.recipientEmail ?? '',
      isNil(result) ? '' : getFinancingEndingOperationItemResultLabel(result),
      reason?.label ?? '',
      formatOptional(item.processedAt),
    ]),
  ];

  return `${BOM}${lines.map(toCsvLine).join(LINE_ENDING)}`;
};
